import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InlineMarkdown from './InlineMarkdown';
import { useAiPanelState } from './AiPanelContext';
import type { AiPanelContextKind } from './AiPanelContext';
import { Bot, Send, Trash2, CreditCard, ChevronRight, ArrowRight, Icon } from '../ui/Icon';
import { GiaLogoGlow, GiaName } from './GiaLogo';
import { useTranslation } from 'react-i18next';
import {
  aiApi,
  type AiAction, type AiMessage, type ResolvedContext, type HintContext,
} from '../../api/ai.api';
import {
  persistVenueProposal, persistVenueDeleteProposal, persistVenueNotesProposal,
  persistActivityProposal, persistActivityMoveProposal,
  persistDayPlanProposal, persistReorderProposal, persistDeleteProposal,
  persistPackingProposal, persistTipsProposal, persistExpenseProposal, persistExpenseDeleteProposal,
  persistBudgetProposal,
  type AiPersistedAction,
} from '../../api/aiProposals';
import {
  persistActivityUpdateProposal, persistPackingItemsProposal, persistNoteProposal, persistTicketProposal,
  persistMealProposal, persistGroceryProposal, persistEntityUpdateProposal, persistEntityDeleteProposal, persistOperationProposal,
} from '../../api/aiTripProposals';
import { TripProposalCard } from './TripProposalCards';
import { isTripPending } from './tripPending';
import {
  type Pending,
  ProposalCard, DayPlanCard, ReorderCard, DeleteCard, VenueDeleteCard, VenueNotesCard, PackingCard, TipsCard,
  ExpenseCard, ExpenseDeleteCard, BudgetCard,
} from './ProposalCards';
import type { Trip } from '../../types';
import { billingApi } from '../../api/billing.api';
import { useAuthStore } from '../../stores/auth.store';
import { toast } from '../ui/Toast';
import Spinner from '../ui/Spinner';

// ─── Persisted callback payload — same shape parents already consume ──────

export type { AiPersistedAction };

// Cap history sent to the model — without this, cost grows ~quadratically
// over a long session (every new turn re-pays for every prior turn's
// tokens). The assistant doesn't need turn #1 once the conversation is 20
// turns deep; the full conversation still displays in the UI regardless.
const MAX_HISTORY_MESSAGES = 12;

// Which tab-domain a persisted proposal lands in — used to offer a "review it"
// shortcut when the AI created something in a section other than the active tab
// (so the user can go see the consequences of accepting). Mirrors
// domainForAction in useAiActionDispatch (that one routes UI updates; this one
// drives navigation). Kept as an AiPanelContextKind so it compares directly to
// the active `context`.
type ReviewDomain = Exclude<AiPanelContextKind, 'general'>;

function reviewDomainForAction(type: AiPersistedAction['type']): ReviewDomain | null {
  switch (type) {
    case 'packing_items_updated':    return 'packing';
    case 'booking_created':          return 'bookings';
    // No review shortcut for these yet: their tabs are not chat contexts.
    case 'note_created':
    case 'meals_updated':
    case 'entity_changed':
    case 'operation_applied':        return null;
    case 'venue_created':
    case 'venue_updated':
    case 'venues_deleted':           return 'places';
    case 'tips_created':             return 'tips';
    case 'packing_sections_created': return 'packing';
    case 'expense_created':
    case 'expenses_deleted':
    case 'budget_set':               return 'expenses';
    default:                         return 'itinerary'; // activity_* / day_plan / reorder
  }
}

// The route segment each domain's tab lives at (relative to /trips/:id). 'places'
// is the Venues tab; 'tips' is rendered inside the Info tab.
const REVIEW_ROUTE: Record<ReviewDomain, string> = {
  places:    'venues',
  itinerary: 'itinerary',
  packing:   'packing',
  tips:      'info',
  expenses:  'expenses',
  bookings:  'bookings',
  allroutes: 'map',
};

interface Props {
  tripId: string;
  trip: Trip;
  context: AiPanelContextKind;
  /** Collapse the panel back to its rail (does not destroy the conversation). */
  onCollapse: () => void;
  onAction: (actions: AiPersistedAction[]) => void;
}

export default function AiChatPanel({ tripId, trip, context, onCollapse, onAction }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const PLACEHOLDERS: Record<Props['context'], string> = {
    places: t('ai.placeholder.places'),
    itinerary: t('ai.placeholder.itinerary'),
    allroutes: t('ai.placeholder.allroutes'),
    packing: t('ai.placeholder.packing'),
    tips: t('ai.placeholder.tips'),
    bookings: t('ai.placeholder.bookings'),
    expenses: t('ai.placeholder.expenses'),
    general: t('ai.placeholder.general'),
  };
  const WELCOME: Record<Props['context'], string> = {
    places: t('ai.welcome.places'),
    itinerary: t('ai.welcome.itinerary'),
    allroutes: t('ai.welcome.allroutes'),
    packing: t('ai.welcome.packing'),
    tips: t('ai.welcome.tips'),
    bookings: t('ai.welcome.bookings'),
    expenses: t('ai.welcome.expenses'),
    general: t('ai.welcome.general'),
  };
  // Labels cover both the active tab context and any context the orchestrator
  // may resolve to (general/routes included).
  const CONTEXT_LABELS: Record<ResolvedContext, string> = {
    places: t('ai.context.places'),
    itinerary: t('ai.context.itinerary'),
    allroutes: t('map.routes'),
    routes: t('map.routes'),
    packing: t('ai.context.packing'),
    tips: t('ai.context.tips'),
    bookings: t('ai.context.bookings'),
    expenses: t('ai.context.expenses'),
    general: t('ai.context.general'),
  };
  // The specialist the orchestrator actually used for the last turn (once known)
  // drives the header; before any turn we show the active tab.
  const [resolved, setResolved] = useState<ResolvedContext | null>(null);
  const labelContext: ResolvedContext = resolved ?? context;
  // ADR-0027: the server picks the model per message, so the label follows the
  // last reply; before any, the tab's usual model.
  const [lastModel, setLastModel] = useState<string | null>(null);
  const usualModel = labelContext === 'places' || labelContext === 'itinerary' || labelContext === 'allroutes' || labelContext === 'routes'
    ? 'gpt-5.6-luna' : 'gpt-4o-mini';
  const modelLabel = (lastModel ?? usualModel) === 'gpt-4o-mini' ? 'GPT-4o mini' : 'GPT-5.6';
  const { user, updateAiCredits, updatePremiumAiCredits } = useAuthStore();
  const [messages, setMessages] = useState<AiMessage[]>([]);
  const [input, setInput] = useState('');
  const { request, clearRequest } = useAiPanelState();
  const [loading, setLoading] = useState(false);
  const [buyingAi, setBuyingAi] = useState(false);
  const [pendings, setPendings] = useState<Pending[]>([]);
  const [batchSaving, setBatchSaving] = useState(false);
  // Sections the AI just created something in that AREN'T the active tab — each
  // gets a "review it there" shortcut so the user can see what accepting did.
  const [reviewTargets, setReviewTargets] = useState<ReviewDomain[]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isAdmin = user?.isAdmin ?? false;
  const premiumCredits = user?.premiumAiCredits ?? 0;
  const freeCredits = user?.aiCredits ?? 0;
  // ADR-0023: PRO's monthly allowance is counted on the server; past it the
  // server answers 402 like any other user out of credits.
  const isPro = user?.isPro ?? false;
  const isPremium = isAdmin || isPro || premiumCredits > 0;
  const activeCredits = isPremium ? premiumCredits : freeCredits;
  const outOfCredits = !isAdmin && !isPro && premiumCredits <= 0 && freeCredits <= 0;

  // Show the active section's welcome line while the conversation is still
  // "fresh" (empty, or only the welcome — a real exchange always starts with a
  // user message, so length 1 + assistant can only be a welcome). Once the user
  // has actually chatted, the conversation persists unchanged across tabs.
  useEffect(() => {
    setMessages(prev =>
      prev.length === 0 || (prev.length === 1 && prev[0].role === 'assistant')
        ? [{ role: 'assistant', content: WELCOME[context] }]
        : prev,
    );
    // Show the current tab's own label again until the next routed turn.
    setResolved(null);
    // Landing on a tab clears its (and any other) pending review shortcuts —
    // you're now looking at the sections, so the "go review" nudge is spent.
    setReviewTargets([]);
  }, [context]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [input]);

  // No per-context gating: the orchestrator can route a message to any
  // specialist regardless of the active tab, so any proposal type may come
  // back. The central dispatcher applies each accepted proposal to the right
  // place (see useAiActionDispatch).
  function ingestActions(actions: AiAction[]) {
    const next: Pending[] = [];
    for (const action of actions) {
      switch (action.type) {
        case 'venue_proposed':
          next.push({ kind: 'venue', id: action.data.pendingId, status: 'idle', data: action.data });
          break;
        case 'venue_delete_proposed':
          next.push({ kind: 'venue_delete', id: action.data.pendingId, status: 'idle', data: action.data });
          break;
        case 'venue_notes_proposed':
          next.push({ kind: 'venue_notes', id: action.data.pendingId, status: 'idle', data: action.data });
          break;
        case 'activity_proposed':
          next.push({ kind: 'activity', id: action.data.pendingId, status: 'idle', data: action.data });
          break;
        case 'activity_move_proposed':
          next.push({ kind: 'activity_move', id: action.data.pendingId, status: 'idle', data: action.data });
          break;
        case 'day_plan_proposed':
          next.push({ kind: 'day_plan', id: action.data.pendingId, status: 'idle', data: action.data });
          break;
        case 'reorder_proposed':
          next.push({ kind: 'reorder', id: action.data.pendingId, status: 'idle', data: action.data });
          break;
        case 'delete_proposed':
          next.push({ kind: 'delete', id: action.data.pendingId, status: 'idle', data: action.data });
          break;
        case 'packing_proposed':
          next.push({ kind: 'packing', id: action.data.pendingId, status: 'idle', data: action.data });
          break;
        case 'tips_proposed':
          next.push({ kind: 'tips', id: action.data.pendingId, status: 'idle', data: action.data });
          break;
        case 'expense_proposed':
          next.push({ kind: 'expense', id: action.data.pendingId, status: 'idle', data: action.data });
          break;
        case 'expense_delete_proposed':
          next.push({ kind: 'expense_delete', id: action.data.pendingId, status: 'idle', data: action.data });
          break;
        case 'budget_proposed':
          next.push({ kind: 'budget', id: action.data.pendingId, status: 'idle', data: action.data });
          break;
        case 'activity_update_proposed':
          next.push({ kind: 'activity_update', id: action.data.pendingId, status: 'idle', data: action.data });
          break;
        case 'packing_items_proposed':
          next.push({ kind: 'packing_items', id: action.data.pendingId, status: 'idle', data: action.data });
          break;
        case 'note_proposed':
          next.push({ kind: 'note', id: action.data.pendingId, status: 'idle', data: action.data });
          break;
        case 'flight_proposed':
          next.push({ kind: 'ticket', id: action.data.pendingId, status: 'idle', data: { kind: 'flight', data: action.data } });
          break;
        case 'hotel_proposed':
          next.push({ kind: 'ticket', id: action.data.pendingId, status: 'idle', data: { kind: 'hotel', data: action.data } });
          break;
        case 'booking_proposed':
          next.push({ kind: 'ticket', id: action.data.pendingId, status: 'idle', data: { kind: 'booking', data: action.data } });
          break;
        case 'meal_proposed':
          next.push({ kind: 'meal', id: action.data.pendingId, status: 'idle', data: action.data });
          break;
        case 'grocery_proposed':
          next.push({ kind: 'grocery', id: action.data.pendingId, status: 'idle', data: action.data });
          break;
        case 'entity_update_proposed':
          next.push({ kind: 'entity_update', id: action.data.pendingId, status: 'idle', data: action.data });
          break;
        case 'entity_delete_proposed':
          next.push({ kind: 'entity_delete', id: action.data.pendingId, status: 'idle', data: action.data });
          break;
        case 'operation_proposed':
          next.push({ kind: 'operation', id: action.data.pendingId, status: 'idle', data: action.data });
          break;
      }
    }
    if (next.length > 0) setPendings(prev => [...prev, ...next]);
  }

  // Returns an array because a single accepted proposal can persist more than
  // one entity — an itinerary activity/day-plan that also creates new venues in
  // Places (see persistActivityProposal / persistDayPlanProposal).
  async function persistOne(p: Pending): Promise<AiPersistedAction[]> {
    if (p.kind === 'activity')      return persistActivityProposal(tripId, p.data);
    if (p.kind === 'day_plan')      return persistDayPlanProposal(tripId, p.data);
    if (p.kind === 'venue')         return [await persistVenueProposal(tripId, p.data)];
    if (p.kind === 'venue_delete')  return [await persistVenueDeleteProposal(tripId, p.data)];
    if (p.kind === 'venue_notes')   return [await persistVenueNotesProposal(tripId, p.data)];
    if (p.kind === 'activity_move') return [await persistActivityMoveProposal(tripId, p.data)];
    if (p.kind === 'reorder')       return [await persistReorderProposal(tripId, p.data)];
    if (p.kind === 'delete')        return [await persistDeleteProposal(tripId, p.data)];
    if (p.kind === 'packing')       return [await persistPackingProposal(tripId, p.data)];
    if (p.kind === 'tips')          return [await persistTipsProposal(tripId, p.data)];
    if (p.kind === 'expense')       return [await persistExpenseProposal(tripId, trip, p.data)];
    if (p.kind === 'expense_delete') return [await persistExpenseDeleteProposal(tripId, p.data)];
    if (p.kind === 'budget')        return [await persistBudgetProposal(tripId, p.data)];
    if (p.kind === 'activity_update') return [await persistActivityUpdateProposal(tripId, p.data)];
    if (p.kind === 'packing_items') return [await persistPackingItemsProposal(tripId, p.data)];
    if (p.kind === 'note')          return [await persistNoteProposal(tripId, p.data)];
    if (p.kind === 'ticket')        return [await persistTicketProposal(tripId, p.data)];
    if (p.kind === 'meal')          return [await persistMealProposal(tripId, p.data)];
    if (p.kind === 'grocery')       return [await persistGroceryProposal(tripId, p.data)];
    if (p.kind === 'entity_update') return [await persistEntityUpdateProposal(tripId, p.data)];
    if (p.kind === 'entity_delete') return [await persistEntityDeleteProposal(tripId, p.data)];
    if (p.kind === 'operation')     return [await persistOperationProposal(tripId, p.data)];
    return [];
  }

  // After a proposal is saved, surface a "review it" shortcut for every section
  // it touched that isn't the tab the user is on right now.
  function noteReviewTargets(persisted: AiPersistedAction[]) {
    const affected = persisted
      .map(a => reviewDomainForAction(a.type))
      .filter((d): d is ReviewDomain => d !== null && d !== context);
    if (affected.length === 0) return;
    setReviewTargets(prev => [...new Set([...prev, ...affected])]);
  }

  async function handleAccept(pendingId: string) {
    const pending = pendings.find(p => p.id === pendingId);
    if (!pending || pending.status !== 'idle') return;
    setPendings(prev => prev.map(p => p.id === pendingId ? { ...p, status: 'saving' } : p));
    // Only the persist (the actual save) decides success/failure. The optimistic
    // UI update runs after and is best-effort — a tab failing to reflect the
    // change must not surface as "save failed" when the entity was created.
    let persisted: AiPersistedAction[];
    try {
      persisted = await persistOne(pending);
    } catch {
      setPendings(prev => prev.map(p => p.id === pendingId ? { ...p, status: 'idle' } : p));
      toast(t('ai.saveFailed'), 'error');
      return;
    }
    setPendings(prev => prev.filter(p => p.id !== pendingId));
    if (persisted.length > 0) { onAction(persisted); noteReviewTargets(persisted); }
  }

  function handleDiscard(pendingId: string) {
    setPendings(prev => prev.filter(p => p.id !== pendingId));
  }

  async function handleAcceptAll() {
    if (batchSaving) return;
    setBatchSaving(true);
    const idle = pendings.filter(p => p.status === 'idle');
    setPendings(prev => prev.map(p => p.status === 'idle' ? { ...p, status: 'saving' } : p));
    const persisted: AiPersistedAction[] = [];
    const failedIds: string[] = [];
    for (const p of idle) {
      try {
        const r = await persistOne(p);
        persisted.push(...r);
      } catch {
        failedIds.push(p.id);
      }
    }
    if (persisted.length > 0) { onAction(persisted); noteReviewTargets(persisted); }
    setPendings(prev => prev.filter(p => failedIds.includes(p.id)).map(p => ({ ...p, status: 'idle' })));
    if (failedIds.length > 0) toast(t('ai.someNotSaved'), 'error');
    setBatchSaving(false);
  }

  async function handleSend(override?: string) {
    const text = (override ?? input).trim();
    if (!text || loading || outOfCredits) return;

    const userMsg: AiMessage = { role: 'user', content: text };
    const next = [
      ...messages.filter(m => (m.role !== 'assistant' || messages.indexOf(m) > 0) && !!m.content),
      userMsg,
    ];
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const pendingNames = pendings
        .filter(p => p.kind === 'venue' && p.status === 'idle')
        .map(p => (p as Extract<typeof p, { kind: 'venue' }>).data.name);

      // The map's route-optimizer keeps its dedicated specialist (bypass the
      // router); every other tab defers to the orchestrator, passing the active
      // tab as a routing hint — none from a tab with no specialist.
      const isAllRoutes = context === 'allroutes';
      const hint = isAllRoutes || context === 'general' ? {} : { hintContext: context as HintContext };
      const { reply, actions, model, resolvedContext, aiCreditsLeft, premiumAiCreditsLeft, mapsQuotaExceeded } = await aiApi.chat(
        tripId,
        next.slice(-MAX_HISTORY_MESSAGES),
        isAllRoutes ? 'allroutes' : 'auto',
        {
          ...(pendingNames.length > 0 ? { pendingNames } : {}),
          ...hint,
        },
      );
      setResolved(resolvedContext);
      setLastModel(model);
      const displayReply = reply || t('ai.error');
      setMessages(prev => [...prev, { role: 'assistant', content: displayReply }]);
      updateAiCredits(aiCreditsLeft);
      updatePremiumAiCredits(premiumAiCreditsLeft);
      if (actions.length > 0) ingestActions(actions);
      if (mapsQuotaExceeded) {
        const date = mapsQuotaExceeded.resetsAt ? new Date(mapsQuotaExceeded.resetsAt).toLocaleDateString() : '';
        toast(t('maps.quotaExceeded', { date }), 'error');
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: t('ai.error') }]);
    } finally {
      setLoading(false);
    }
  }

  async function handleBuyAi() {
    setBuyingAi(true);
    try {
      const { url } = await billingApi.createProCheckout();
      window.location.href = url;
    } catch {
      toast(t('billing.aiPaywall.error'), 'error');
      setBuyingAi(false);
    }
  }

  // A prompt asked for from the itinerary (or a suggestion chip). Keyed on the
  // nonce, not the text: asking the same thing twice has to ask twice.
  useEffect(() => {
    if (!request) return;
    clearRequest();
    if (request.send) { handleSend(request.text); return; }
    setInput(request.text);
    textareaRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.nonce]);

  const idleCount = pendings.filter(p => p.status === 'idle').length;
  // Shown only on a conversation that has not started: once there are messages
  // the user knows what to say, and the chips would be in the way.
  const suggestions = t(`ai.suggestions.${context}`, { returnObjects: true, defaultValue: [] }) as string[];
  const showSuggestions = messages.length <= 1 && pendings.length === 0 && !loading;

  return (
    <div className="flex flex-col h-full w-full bg-white dark:bg-slate-900">

      {/* Header */}
      <div className="shrink-0 px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3">
        <GiaLogoGlow size={32} className="shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 leading-none"><GiaName /></p>
          <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5 leading-none">
            {modelLabel} · {CONTEXT_LABELS[labelContext]}
          </p>
        </div>
        {!isAdmin && (
          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${
            outOfCredits
              ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
              : activeCredits <= 5
              ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
              : isPremium
              ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400'
              : 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400'
          }`}>
            {isPro ? 'PRO' : activeCredits}
          </span>
        )}
        <div className="flex items-center gap-1">
          <button
            onClick={() => { setMessages([{ role: 'assistant', content: WELCOME[context] }]); setPendings([]); setResolved(null); setReviewTargets([]); }}
            className="w-7 h-7 rounded-lg text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center transition-colors cursor-pointer"
            title={t('ai.clearConversation')}
          >
            <Icon as={Trash2} size={14} />
          </button>
          <button
            onClick={onCollapse}
            className="w-7 h-7 rounded-lg text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center transition-colors cursor-pointer"
            title={t('ai.collapse')}
            aria-label={t('ai.collapse')}
          >
            <Icon as={ChevronRight} size={16} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {messages.map((msg, i) => (
          <div key={i} className={`flex items-end gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
            {msg.role === 'assistant' && (
              <div className="w-6 h-6 rounded-full bg-teal-50 dark:bg-teal-900/40 border border-teal-100 dark:border-teal-800 flex items-center justify-center shrink-0 mb-0.5">
                <Icon as={Bot} size={14} className="text-teal-500 dark:text-teal-400" />
              </div>
            )}
            <div className={`max-w-[78%] text-[13px] leading-relaxed whitespace-pre-line ${
              msg.role === 'user'
                ? 'bg-teal-500 text-white px-3.5 py-2.5 rounded-2xl rounded-br-md shadow-sm'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 px-3.5 py-2.5 rounded-2xl rounded-bl-md'
            }`}>
              <InlineMarkdown text={msg.content} />
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex items-end gap-2">
            <div className="w-6 h-6 rounded-full bg-teal-50 dark:bg-teal-900/40 border border-teal-100 dark:border-teal-800 flex items-center justify-center shrink-0 mb-0.5">
              <Icon as={Bot} size={14} className="text-teal-500 dark:text-teal-400" />
            </div>
            <div className="bg-slate-100 dark:bg-slate-800 px-4 py-3 rounded-2xl rounded-bl-md flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 dark:bg-slate-500 animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 dark:bg-slate-500 animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 dark:bg-slate-500 animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}

        {/* Proposals */}
        {pendings.length > 0 && (
          <div className="space-y-2">
            {pendings.map(p => {
              const shared = { key: p.id, onAccept: () => handleAccept(p.id), onDiscard: () => handleDiscard(p.id) };
              if (p.kind === 'day_plan') return <DayPlanCard {...shared} pending={p} tripId={tripId} />;
              if (p.kind === 'reorder')   return <ReorderCard {...shared} pending={p} />;
              if (p.kind === 'delete')    return <DeleteCard {...shared} pending={p} />;
              if (p.kind === 'venue_delete') return <VenueDeleteCard {...shared} pending={p} />;
              if (p.kind === 'venue_notes')  return <VenueNotesCard {...shared} pending={p} />;
              if (p.kind === 'packing')   return <PackingCard {...shared} pending={p} tripId={tripId} />;
              if (p.kind === 'tips')      return <TipsCard {...shared} pending={p} />;
              if (p.kind === 'expense')   return <ExpenseCard {...shared} pending={p} baseCurrency={trip.budgetCurrency || 'EUR'} />;
              if (p.kind === 'expense_delete') return <ExpenseDeleteCard {...shared} pending={p} baseCurrency={trip.budgetCurrency || 'EUR'} />;
              if (p.kind === 'budget')    return <BudgetCard {...shared} pending={p} baseCurrency={trip.budgetCurrency || 'EUR'} />;
              if (isTripPending(p))       return <TripProposalCard {...shared} pending={p} />;
              return <ProposalCard {...shared} pending={p} tripId={tripId} />;
            })}
            {/* Accept-all lives BELOW the cards so the flow stays chronological:
                read the proposals top-to-bottom, then the batch action at the end. */}
            {idleCount > 1 && (
              <div className="flex items-center justify-between px-1 pt-0.5">
                <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                  {t('ai.proposals_other', { count: idleCount })}
                </p>
                <button
                  onClick={handleAcceptAll}
                  disabled={batchSaving}
                  className="text-[11px] font-semibold text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 disabled:opacity-50 cursor-pointer"
                >
                  {batchSaving ? t('ai.saving') : t('ai.acceptAll')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Cross-section review shortcuts: the AI saved something in a tab other
            than this one — offer a jump so the user can see what accepting did. */}
        {reviewTargets.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {reviewTargets.map(d => (
              <button
                key={d}
                onClick={() => navigate(`/trips/${tripId}/${REVIEW_ROUTE[d]}`)}
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-full bg-teal-50 dark:bg-teal-900/30 border border-teal-200 dark:border-teal-800 text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-900/50 transition-colors cursor-pointer"
              >
                {t(`ai.review.${d}` as Parameters<typeof t>[0])}
                <Icon as={ArrowRight} size={13} />
              </button>
            ))}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Everything the assistant can do is invisible until you know the words
          for it. These are the words. */}
      {showSuggestions && Array.isArray(suggestions) && suggestions.length > 0 && !outOfCredits && (
        <div className="shrink-0 px-3 pb-1 flex flex-wrap gap-1.5">
          {suggestions.map(s => (
            <button
              key={s}
              type="button"
              onClick={() => { setInput(s); textareaRef.current?.focus(); }}
              className="text-[11px] px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-teal-50 dark:hover:bg-teal-900/30 hover:text-teal-700 dark:hover:text-teal-300 border border-transparent hover:border-teal-200 dark:hover:border-teal-800 transition-colors cursor-pointer"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Input area */}
      <div className="shrink-0 px-3 pb-3 pt-2 border-t border-slate-100 dark:border-slate-800">
        {outOfCredits ? (
          <div className="rounded-2xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-3 text-center space-y-2.5">
            <p className="text-xs text-amber-700 dark:text-amber-400 leading-snug">
              {t('billing.aiPaywall.description')}
            </p>
            <button onClick={handleBuyAi} disabled={buyingAi} className="btn-primary w-full gap-1.5 text-xs py-1.5">
              {buyingAi ? <Spinner size="sm" /> : <Icon as={CreditCard} size={14} />}
              {t('billing.aiPaywall.cta')}
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-end gap-2 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-2xl px-3 py-2 focus-within:border-teal-400 dark:focus-within:border-teal-500 focus-within:ring-1 focus-within:ring-teal-400/30 transition-all">
              <textarea
                ref={textareaRef}
                className="flex-1 resize-none bg-transparent text-sm text-slate-800 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none leading-relaxed min-h-[24px] max-h-[120px] py-0.5"
                rows={1}
                placeholder={PLACEHOLDERS[context]}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
                }}
              />
              <button
                onClick={() => handleSend()}
                disabled={!input.trim() || loading}
                className="w-8 h-8 rounded-xl bg-teal-500 hover:bg-teal-600 disabled:bg-slate-200 dark:disabled:bg-slate-700 flex items-center justify-center transition-colors cursor-pointer shrink-0 shadow-sm"
              >
                <Icon as={Send} size={14} className="text-white dark:text-slate-400" />
              </button>
            </div>
            <p className="text-[10px] text-slate-400 dark:text-slate-600 mt-1.5 text-center">{t('ai.sendHint')}</p>
          </>
        )}
      </div>
    </div>
  );
}
